import React, { useEffect } from "react"
import ReactDOM from "react-dom"
import moment from "moment"

import { formatCurrency } from "~/utils/helpers"
import Booking from "~/types/Booking"
import type Pack from "~/types/Pack"
import type PackItem from "~/types/PackItem"
import type BookingItem from "~/types/BookingItem"

const emptyMoney = { cents: 0, currency_iso: "USD" }

interface BookingPrintReceiptProps {
  booking: Booking
  onClose: () => void
}

const BookingPrintReceipt = ({ booking, onClose }: BookingPrintReceiptProps) => {
  useEffect(() => {
    const handleAfterPrint = () => {
      onClose()
    }

    window.addEventListener("afterprint", handleAfterPrint)
    window.print()

    return () => {
      window.removeEventListener("afterprint", handleAfterPrint)
    }
  }, [onClose])

  const hasSurcharge = (booking.bookingSurchargeAmountCents || 0) > 0
  const hasTax = (booking.taxAmountCents || 0) > 0
  const hasPacks = (booking.packs || []).length > 0
  const hasLineItems = (booking.bookingItems || []).length > 0

  const receipt = (
    <div id="booking-print-receipt">
      <div className="receipt-header">
        <h1>{booking.name}</h1>
        <p className="receipt-meta">{moment(booking.date).format("MMMM Do, YYYY")}</p>
        {booking.area?.name && (
          <p className="receipt-meta">{booking.area.name}</p>
        )}
      </div>

      <div className="receipt-section">
        <div className="receipt-section-title">Booking Details</div>
        <div className="receipt-detail-row">
          <span className="receipt-detail-label">Client</span>
          <span className="receipt-detail-value">{booking.client?.name || "—"}</span>
        </div>
        {booking.client?.email && (
          <div className="receipt-detail-row">
            <span className="receipt-detail-label">Email</span>
            <span className="receipt-detail-value">{booking.client.email}</span>
          </div>
        )}
        {booking.client?.phone && (
          <div className="receipt-detail-row">
            <span className="receipt-detail-label">Phone</span>
            <span className="receipt-detail-value">{booking.client.phone}</span>
          </div>
        )}
        {booking.details && (
          <div className="receipt-detail-row">
            <span className="receipt-detail-label">Details</span>
            <span className="receipt-detail-value">{booking.details}</span>
          </div>
        )}
      </div>

      {hasPacks && (
        <div className="receipt-section">
          <div className="receipt-section-title">Packs</div>
          <table className="receipt-table">
            <thead>
              <tr>
                <th>Pack</th>
                <th style={{ textAlign: "right" }}>Price</th>
              </tr>
            </thead>
            <tbody>
              {(booking.packs || []).map((pack: Pack) => (
                <tr key={pack.id}>
                  <td>
                    <div className="pack-name">{pack.name}</div>
                    {pack.items && pack.items.length > 0 && (
                      <div className="item-meta">
                        {pack.items
                          .map((item: PackItem) =>
                            item.quantity > 1
                              ? `${item.lineItem?.name} ×${item.quantity}`
                              : item.lineItem?.name
                          )
                          .join(", ")}
                      </div>
                    )}
                  </td>
                  <td>{formatCurrency(pack.price || emptyMoney)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasLineItems && (
        <div className="receipt-section">
          <div className="receipt-section-title">Line Items</div>
          <table className="receipt-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th>Unit Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {(booking.bookingItems || []).map((item: BookingItem) => (
                <tr key={item.id}>
                  <td>
                    <div className="item-name">{item.name}</div>
                    {item.categoryName && (
                      <div className="item-meta">{item.categoryName}</div>
                    )}
                    {item.modifierDetails && item.modifierDetails.length > 0 && (
                      <div className="item-meta">
                        {item.modifierDetails
                          .map((m) => (m.quantity > 1 ? `${m.name} ×${m.quantity}` : m.name))
                          .join(", ")}
                      </div>
                    )}
                  </td>
                  <td>{item.quantity}</td>
                  <td>{formatCurrency(item.amount || emptyMoney)}</td>
                  <td>{formatCurrency(item.subtotal || emptyMoney)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="receipt-totals">
        <div className="receipt-total-row subtotal-row">
          <span>Subtotal</span>
          <span>{formatCurrency(booking.subtotalAmount || emptyMoney)}</span>
        </div>

        {hasSurcharge && (
          <div className="receipt-total-row surcharge-row">
            <span>Service Charge ({booking.bookingSurchargeRate || 0}%)</span>
            <span>{formatCurrency(booking.bookingSurchargeAmount || emptyMoney)}</span>
          </div>
        )}

        {hasTax && (
          <div className="receipt-total-row tax-row">
            <span>Tax ({booking.taxRate || 0}%)</span>
            <span>{formatCurrency(booking.taxAmount || emptyMoney)}</span>
          </div>
        )}

        <div className="receipt-total-row grand-total">
          <span>Total</span>
          <span>{formatCurrency(booking.totalAmount || emptyMoney)}</span>
        </div>
      </div>

      <div className="tip-section">
        <div className="tip-row">
          <span>Tip</span>
          <div className="tip-line" />
          <div className="tip-amount-box">
            <span>$</span>
            <div className="tip-amount-line" />
          </div>
        </div>
      </div>

      <div className="receipt-footer">
        <div className="thank-you">Thank you!</div>
        <div>Printed on {moment().format("MMMM Do, YYYY [at] h:mm a")}</div>
      </div>
    </div>
  )

  return ReactDOM.createPortal(
    <div id="booking-print-receipt-portal">{receipt}</div>,
    document.body
  )
}

export default BookingPrintReceipt